import { useCallback, useRef, useEffect } from 'react';

/**
 * Creates a debounced callback that delays invoking the provided function
 * until after `delay` milliseconds have elapsed since the last time it was invoked.
 * 
 * Uses a stable ref pattern to prevent the returned function from changing on every render.
 * 
 * @param callback - The function to debounce
 * @param delay - The number of milliseconds to delay (default: 150ms)
 * @returns A debounced version of the callback
 */
export function useDebounceCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number = 150
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);
  
  // Update ref when callback changes (doesn't trigger re-render)
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as T,
    [delay] // Only delay in dependencies - callback stored in ref
  );
}
